/**
 * OAuth redirect URL builders and CSRF state management.
 *
 * Each provider function builds the auth URL and redirects the browser.
 * Google/LinkedIn use standard OAuth. Microsoft uses PKCE (code challenge).
 */

interface OAuthState {
	nonce: string;
	intent: 'client' | 'talent';
}

// crypto.randomUUID is missing in older browsers and insecure contexts; getRandomValues is not.
function randomId(): string {
	if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
	const bytes = crypto.getRandomValues(new Uint8Array(16));
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
	return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

export function buildOauthState(intent: 'client' | 'talent' = 'client'): string {
	const nonce = randomId();
	sessionStorage.setItem('oauth_nonce', nonce);
	return JSON.stringify({ nonce, intent });
}

/**
 * Parse OAuth state from the callback URL search params.
 * TanStack Router auto-parses JSON search params, so state arrives
 * as an already-parsed object (not a string).
 */
export function parseOauthState(state: OAuthState): {
	nonce: string;
	intent: 'client' | 'talent';
} | null {
	if (typeof state?.nonce === 'string') {
		return {
			nonce: state.nonce,
			intent: state.intent === 'talent' ? 'talent' : 'client',
		};
	}
	return null;
}

export function validateOauthNonce(state: OAuthState): {
	intent: 'client' | 'talent';
} | null {
	const parsed = parseOauthState(state);
	const sessionNonce = sessionStorage.getItem('oauth_nonce');

	if (!parsed || parsed.nonce !== sessionNonce) return null;

	sessionStorage.removeItem('oauth_nonce');
	return { intent: parsed.intent };
}

export function redirectToGoogle(intent: 'client' | 'talent' = 'client') {
	const state = buildOauthState(intent);
	const url = new URL('https://accounts.google.com/o/oauth2/auth');
	url.searchParams.set('response_type', 'code');
	url.searchParams.set('client_id', import.meta.env.VITE_GOOGLE_CLIENT_ID);
	url.searchParams.set('redirect_uri', `${window.location.origin}/oauth/google`);
	url.searchParams.set('scope', 'email profile openid');
	url.searchParams.set('state', state);
	window.location.href = url.toString();
}

export function redirectToLinkedIn(intent: 'client' | 'talent' = 'client') {
	const state = buildOauthState(intent);
	const url = new URL('https://www.linkedin.com/oauth/v2/authorization');
	url.searchParams.set('response_type', 'code');
	url.searchParams.set('client_id', import.meta.env.VITE_LINKEDIN_CLIENT_ID);
	url.searchParams.set('redirect_uri', `${window.location.origin}/oauth/linkedin`);
	url.searchParams.set('scope', 'profile email openid');
	url.searchParams.set('state', state);
	window.location.href = url.toString();
}

export async function redirectToMicrosoft(intent: 'client' | 'talent' = 'client') {
	const state = buildOauthState(intent);

	const codeVerifier = randomId();
	sessionStorage.setItem('oauth_code_verifier', codeVerifier);
	const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
	const codeChallenge = base64urlencode(digest);

	const url = new URL(
		`https://login.microsoftonline.com/${import.meta.env.VITE_MICROSOFT_TENANT_ID}/oauth2/v2.0/authorize`,
	);
	url.searchParams.set('response_type', 'code');
	url.searchParams.set('client_id', import.meta.env.VITE_MICROSOFT_CLIENT_ID);
	url.searchParams.set('redirect_uri', `${window.location.origin}/oauth/microsoft`);
	url.searchParams.set('scope', 'profile email openid');
	url.searchParams.set('state', state);
	url.searchParams.set('code_challenge', codeChallenge);
	url.searchParams.set('code_challenge_method', 'S256');
	url.searchParams.set('response_mode', 'query');
	window.location.href = url.toString();
}

function base64urlencode(buffer: ArrayBuffer): string {
	return btoa(String.fromCharCode(...new Uint8Array(buffer)))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '');
}
